import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart as RechartsPieChart, Pie
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import {
  Database,
  FileText,
  Link,
  PieChart,
  Activity,
  Trash2,
  Loader2,
  AlertTriangle,
  Upload,
  RefreshCw,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { useChartTokens } from '@/hooks/useChartTokens';
// Task #1416 — sezione Pipeline Health condivisa (variante compact, scope agente).
import { PipelineHealthSection } from '@/components/ai/rag-health/PipelineHealthSection';

type EmbeddingStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'dead_letter' | 'skipped' | 'paused_quota';

interface AIDocument {
  id: string;
  filename: string;
  file_type?: string;
  chunk_count: number;
  status?: string;
  ocr_status?: string;
  error_message?: string | null;
  embedding_status?: EmbeddingStatus | null;
  embedding_progress_pct?: number | null;
  embedding_total_pages?: number | null;
  embedding_processed_pages?: number | null;
  embedding_failed_pages?: Array<{ page: number; error: string; attempts?: number }> | null;
  embedding_last_error?: string | null;
  multimodal_bypass?: boolean;
}

interface AIURL {
  id: string;
  url: string;
  title?: string;
  chunk_count?: number;
}

interface KBStatsResponse {
  totalDocuments: number;
  totalUrls: number;
  totalChunks: number;
  totalTokens: number;
  bySource?: Record<string, { chunks: number; tokens: number }>;
}

interface DocumentRow {
  id: string;
  name: string;
  chunks: number;
  status: string;
  fileType: string;
  ocrStatus: string;
  embeddingStatus: EmbeddingStatus | null;
  embeddingPct: number;
  embeddingTotalPages: number;
  embeddingProcessedPages: number;
  embeddingFailedPages: number;
  embeddingLastError: string | null;
  errorMessage: string | null;
  multimodalBypass: boolean;
}

interface RagStats {
  totalDocuments: number;
  totalUrls: number;
  totalChunks: number;
  totalTokens: number;
  bySource: Record<string, { chunks: number; tokens: number }>;
  documentBreakdown: DocumentRow[];
  urlBreakdown: { id: string; title: string; url: string; chunks: number }[];
}

interface RAGAnalyticsDashboardProps {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function RAGAnalyticsDashboard({ agentId, open, onOpenChange }: RAGAnalyticsDashboardProps) {
  const { toast } = useToast();
  const tokens = useChartTokens();
  const tooltipStyle = { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: '8px', color: tokens.fg } as const;
  const labelStyle = { color: tokens.fg } as const;
  const [ragStats, setRagStats] = useState<RagStats | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'document' | 'url'; id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReUploading, setIsReUploading] = useState<string | null>(null);
  const reUploadInputRef = useRef<HTMLInputElement>(null);
  const [reUploadDocId, setReUploadDocId] = useState<string | null>(null);

  const loadRagAnalytics = useCallback(async () => {
    try {
      const [docsRes, urlsRes, statsRes] = await Promise.all([
        apiClient.get<AIDocument[]>(`/knowledge-base/${agentId}/documents`),
        apiClient.get<AIURL[]>(`/knowledge-base/${agentId}/urls`),
        apiClient.get<KBStatsResponse>(`/knowledge-base/${agentId}/stats`)
      ]);

      const docs = docsRes.data || [];
      const urls = urlsRes.data || [];
      const stats: KBStatsResponse = statsRes.data || { totalDocuments: 0, totalUrls: 0, totalChunks: 0, totalTokens: 0 };

      setRagStats({
        totalDocuments: stats.totalDocuments ?? docs.length,
        totalUrls: stats.totalUrls ?? urls.length,
        totalChunks: stats.totalChunks ?? 0,
        totalTokens: stats.totalTokens ?? 0,
        bySource: stats.bySource ?? {},
        documentBreakdown: docs.map((d: AIDocument) => ({
          id: d.id,
          name: d.filename,
          chunks: d.chunk_count || 0,
          status: d.status || 'unknown',
          fileType: d.file_type || '',
          ocrStatus: d.ocr_status || 'not_needed',
          embeddingStatus: d.embedding_status ?? null,
          embeddingPct: Math.max(0, Math.min(100, d.embedding_progress_pct ?? 0)),
          embeddingTotalPages: d.embedding_total_pages ?? 0,
          embeddingProcessedPages: d.embedding_processed_pages ?? 0,
          embeddingFailedPages: d.embedding_failed_pages?.length ?? 0,
          embeddingLastError: d.embedding_last_error ?? null,
          errorMessage: d.error_message ?? null,
          multimodalBypass: d.multimodal_bypass === true,
        })),
        urlBreakdown: urls.map((u: AIURL) => ({ id: u.id, title: u.title || u.url, url: u.url, chunks: u.chunk_count || 0 }))
      });
    } catch (error) {
      console.error('Error loading RAG analytics:', error);
      toast({ title: 'Errore', description: 'Impossibile caricare statistiche RAG', variant: 'destructive' });
    }
  }, [agentId, toast]);

  useEffect(() => {
    if (open && agentId) {
      loadRagAnalytics();
    }
    if (!open) {
      setRagStats(null);
      setDeleteConfirm(null);
    }
  }, [open, agentId, loadRagAnalytics]);

  const deleteRagItem = async () => {
    if (!deleteConfirm) return;

    setIsDeleting(true);
    try {
      const endpoint = deleteConfirm.type === 'document'
        ? `/knowledge-base/${agentId}/documents/${deleteConfirm.id}`
        : `/knowledge-base/${agentId}/urls/${deleteConfirm.id}`;

      await apiClient.delete(endpoint);

      toast({
        title: 'Eliminato',
        description: `${deleteConfirm.type === 'document' ? 'Documento' : 'URL'} "${deleteConfirm.name}" eliminato con successo`
      });

      await loadRagAnalytics();
      setDeleteConfirm(null);
    } catch (error: unknown) {
      console.error('Error deleting RAG item:', error);
      const errMsg = error instanceof Error ? error.message : 'Impossibile eliminare';
      toast({
        title: 'Errore',
        description: errMsg,
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReUpload = (docId: string) => {
    setReUploadDocId(docId);
    reUploadInputRef.current?.click();
  };

  const handleReUploadFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !reUploadDocId) return;

    const docIdToReplace = reUploadDocId;
    setIsReUploading(docIdToReplace);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/knowledge-base/${agentId}/documents`, formData);

      await apiClient.delete(`/knowledge-base/${agentId}/documents/${docIdToReplace}`).catch(() => {});

      toast({ title: 'Successo', description: `Documento "${file.name}" ri-caricato e indicizzato` });
      await loadRagAnalytics();
    } catch (error: unknown) {
      console.error('Error re-uploading document:', error);
      const errMsg = error instanceof Error ? error.message : 'Errore durante il ri-caricamento';
      toast({ title: 'Errore', description: errMsg, variant: 'destructive' });
    } finally {
      setIsReUploading(null);
      setReUploadDocId(null);
      if (reUploadInputRef.current) {
        reUploadInputRef.current.value = '';
      }
    }
  };

  const renderPieLabel = (props: PieLabelRenderProps) => {
    const pct = typeof props.percent === 'number' ? props.percent : 0;
    return `${(pct * 100).toFixed(0)}%`;
  };

  // Task #2766 — la torta "Chunks per Tipo" legge la ripartizione canonica
  // `bySource` di /stats (stessi chunk attivi del totale), non la somma delle
  // liste documenti/URL (che escludeva tenant_context e immagini).
  const sourceTypeMeta: Record<string, { label: string; fill: string }> = {
    document: { label: 'Documenti', fill: tokens.google },
    url: { label: 'URL', fill: tokens.openai },
    tenant_context: { label: 'Contesto', fill: tokens.elevenlabs },
    image: { label: 'Immagini', fill: tokens.claude },
  };
  const sourceChunkSlices = Object.entries(ragStats?.bySource ?? {})
    .map(([key, v]) => ({
      key,
      name: sourceTypeMeta[key]?.label ?? key,
      value: v.chunks,
      fill: sourceTypeMeta[key]?.fill ?? tokens.muted,
    }))
    .filter((s) => s.value > 0);

  return (
    <>
      <input
        ref={reUploadInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.md,.csv"
        className="hidden"
        onChange={handleReUploadFileSelect}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-purple-400" />
              RAG Analytics
            </DialogTitle>
            <DialogDescription>Statistiche e qualità della Knowledge Base</DialogDescription>
          </DialogHeader>

          {ragStats && (
            <div className="space-y-6 py-4 overflow-y-auto flex-1">
              {(() => {
                // Task #2766 — score basato su segnali reali di ingestion, non
                // su una formula chunks/100 arbitraria. Conta fonti "pronte"
                // (embedding completato o bypass multimodale con chunk) vs fallite
                // vs in lavorazione, su documenti + URL.
                const docs = ragStats.documentBreakdown;
                const failedDocs = docs.filter(
                  (d) => d.status === 'failed' || d.embeddingStatus === 'failed' || d.embeddingStatus === 'dead_letter'
                ).length;
                const pendingDocs = docs.filter(
                  (d) =>
                    d.embeddingStatus === 'queued' ||
                    d.embeddingStatus === 'processing' ||
                    d.embeddingStatus === 'paused_quota'
                ).length;
                const readyDocs = docs.length - failedDocs - pendingDocs;
                const urlsReady = ragStats.urlBreakdown.filter((u) => u.chunks > 0).length;
                const urlsPending = Math.max(0, ragStats.totalUrls - urlsReady);
                const totalContent = docs.length + ragStats.totalUrls;
                const readyCount = readyDocs + urlsReady;
                const failedCount = failedDocs;
                const pendingCount = pendingDocs + urlsPending;
                const hasContent = totalContent > 0;
                const qualityScore = hasContent ? Math.round((readyCount / totalContent) * 100) : 0;
                const qualityLevel = qualityScore < 20 ? 'Bassa' : qualityScore < 60 ? 'Media' : 'Alta';
                const qualityColor = qualityScore < 20
                  ? 'hsl(var(--destructive))'
                  : qualityScore < 60
                  ? 'var(--status-warning)'
                  : 'var(--status-success)';
                const qualityBg = !hasContent
                  ? 'bg-muted/40'
                  : qualityScore < 20
                  ? 'bg-red-500/15'
                  : qualityScore < 60
                  ? 'bg-yellow-500/15'
                  : 'bg-green-500/15';

                return (
                  <div className={`p-4 rounded-xl ${qualityBg} border border-border`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-foreground">Qualità Knowledge Base</h4>
                      {hasContent ? (
                        <Badge style={{ backgroundColor: qualityColor }} className="text-white">
                          {qualityLevel}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Vuota</Badge>
                      )}
                    </div>
                    {hasContent ? (
                      <>
                        <div className="relative h-4 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${qualityScore}%`,
                              background: `linear-gradient(90deg, hsl(var(--destructive)) 0%, var(--status-warning) 50%, var(--status-success) 100%)`
                            }}
                          />
                          <div className="absolute inset-0 flex justify-between px-1 text-[10px] text-muted-foreground">
                            <span>Bassa</span>
                            <span>Media</span>
                            <span>Alta</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                          <span className="text-green-400">{readyCount} pronte</span>
                          {pendingCount > 0 && <span className="text-yellow-400">{pendingCount} in lavorazione</span>}
                          {failedCount > 0 && <span className="text-red-400">{failedCount} fallite</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Percentuale di fonti (documenti + URL) indicizzate con successo su {totalContent} totali.
                          Le fonti fallite o in errore vanno ri-caricate per alzare il punteggio.
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Nessuna fonte indicizzata per questo agente. Aggiungi documenti o URL per attivare il RAG.
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-400" />
                    <span className="text-xs text-muted-foreground">Documenti</span>
                  </div>
                  <p className="text-xl font-bold text-blue-400">{ragStats.totalDocuments}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4 text-green-400" />
                    <span className="text-xs text-muted-foreground">URL</span>
                  </div>
                  <p className="text-xl font-bold text-green-400">{ragStats.totalUrls}</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                  <div className="flex items-center gap-2">
                    <PieChart className="h-4 w-4 text-purple-400" />
                    <span className="text-xs text-muted-foreground">Chunks</span>
                  </div>
                  <p className="text-xl font-bold text-purple-400">{ragStats.totalChunks}</p>
                </div>
                <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-orange-400" />
                    <span className="text-xs text-muted-foreground">Token</span>
                  </div>
                  <p className="text-xl font-bold text-orange-400">{ragStats.totalTokens.toLocaleString()}</p>
                </div>
              </div>

              {(ragStats.totalDocuments > 0 || ragStats.totalUrls > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-card border border-border">
                    <h4 className="text-sm font-medium text-foreground mb-3">Distribuzione Sorgenti</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={[
                        { name: 'Documenti', value: ragStats.totalDocuments, color: tokens.google },
                        { name: 'URL', value: ragStats.totalUrls, color: tokens.openai },
                      ]}>
                        <XAxis dataKey="name" tick={{ fill: tokens.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: tokens.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          labelStyle={labelStyle}
                        />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {[
                            { name: 'Documenti', value: ragStats.totalDocuments, color: tokens.google },
                            { name: 'URL', value: ragStats.totalUrls, color: tokens.openai },
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="p-4 rounded-lg bg-card border border-border">
                    <h4 className="text-sm font-medium text-foreground mb-3">Chunks per Tipo</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <RechartsPieChart>
                        <Pie
                          data={sourceChunkSlices}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={55}
                          paddingAngle={2}
                          dataKey="value"
                          label={renderPieLabel}
                          labelLine={false}
                        >
                          {sourceChunkSlices.map((slice) => (
                            <Cell key={slice.key} fill={slice.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={tooltipStyle}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Task #1416 — Pipeline Health condivisa, scope agente */}
              <PipelineHealthSection variant="compact" agentId={agentId} />

              {ragStats.documentBreakdown.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-foreground">Dettaglio Documenti</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {ragStats.documentBreakdown.map((doc) => {
                      // Task #2766 — badge di stato coerenti col KnowledgeBaseManager:
                      // distingue fallito / in elaborazione / bypass multimodale / OCR.
                      const isFailed =
                        doc.status === 'failed' ||
                        doc.embeddingStatus === 'failed' ||
                        doc.embeddingStatus === 'dead_letter';
                      const isProcessing =
                        doc.embeddingStatus === 'queued' ||
                        doc.embeddingStatus === 'processing' ||
                        doc.embeddingStatus === 'paused_quota';
                      const isPdf = doc.fileType.toLowerCase().includes('pdf') || doc.name.toLowerCase().endsWith('.pdf');
                      const isMultimodal =
                        doc.multimodalBypass || (doc.ocrStatus === 'not_needed' && isPdf && doc.chunks === 0);
                      const tooltip = isFailed
                        ? (doc.embeddingLastError || doc.errorMessage || 'Indicizzazione fallita')
                        : undefined;
                      return (
                      <div key={doc.id} title={tooltip} className={`flex items-center justify-between p-2 rounded border group ${isFailed ? 'bg-red-500/10 border-red-500/30' : 'bg-card border-border'}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className={`h-3 w-3 flex-shrink-0 ${isFailed ? 'text-red-400' : 'text-blue-400'}`} />
                          <span className="text-sm truncate">{doc.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isFailed ? (
                            <>
                              <Badge variant="outline" className="text-xs text-red-500 border-red-300">Da ri-caricare</Badge>
                              <button
                                onClick={() => handleReUpload(doc.id)}
                                disabled={isReUploading === doc.id}
                                className="p-1 rounded hover:bg-amber-500/20 text-amber-500 transition-opacity"
                                aria-label="Ri-carica documento"
                              >
                                {isReUploading === doc.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                              </button>
                            </>
                          ) : isProcessing ? (
                            <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-400">
                              In elaborazione{doc.embeddingPct > 0 ? ` ${doc.embeddingPct}%` : ''}
                            </Badge>
                          ) : isMultimodal ? (
                            <Badge variant="outline" className="text-xs text-purple-400 border-purple-400/40">Multimodale</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">{doc.chunks} chunks</Badge>
                          )}
                          <button
                            onClick={() => setDeleteConfirm({ type: 'document', id: doc.id, name: doc.name })}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-red-400 transition-opacity"
                            aria-label="Elimina documento"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {ragStats.urlBreakdown.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-foreground">Dettaglio URL</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {ragStats.urlBreakdown.map((url) => (
                      <div key={url.id} className="flex items-center justify-between p-2 rounded bg-card border border-border group">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Link className="h-3 w-3 text-green-400 flex-shrink-0" />
                          <span className="text-sm truncate" title={url.url}>{url.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{url.chunks} chunks</Badge>
                          <button
                            onClick={() => setDeleteConfirm({ type: 'url', id: url.id, name: url.title })}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-red-400 transition-opacity"
                            aria-label="Elimina URL"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ragStats.totalChunks === 0 && (
                <div className="text-center py-6 text-muted-foreground">
                  <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nessun dato nella Knowledge Base</p>
                  <p className="text-sm">Aggiungi documenti o URL per arricchire l'agente</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Conferma Eliminazione
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare {deleteConfirm?.type === 'document' ? 'il documento' : 'l\'URL'}:
              <span className="font-medium text-foreground block mt-2 truncate">"{deleteConfirm?.name}"</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Questa azione eliminerà permanentemente tutti i chunks associati dalla Knowledge Base.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={isDeleting}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={deleteRagItem}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Elimina
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

